import { t } from 'i18next';
import { partition, orderBy, concat, get, difference } from 'lodash';

import { Checkboxes, DemandOptions, OptimizationConfiguration } from '../../types/models';
import { BlockingListState } from '../../state/blockingList/blockingList.types';
import { FacingGroup, LocatedFixture } from '../../state/job/job.types';
import { FixtureSpace } from '../../types/editor';

export type ForecastKeys = {
  avgReg: boolean,
  avgRegPromo: boolean,
  avgRegIss: boolean,
  avgRegPromoIss: boolean,
  peakReg: boolean,
  peakRegPromo: boolean,
  peakRegIss: boolean,
  peakRegPromoIss: boolean
};

const FIRST_LEVEL_HEADER = (
  props: Checkboxes,
  optConf: OptimizationConfiguration,
  blockingList: BlockingListState,
  isPostSolver = false,
  index = 0
) => {
  const {
    avgReg,
    avgRegPromo,
    avgRegIss,
    avgRegPromoIss,
    peakReg,
    peakRegPromo,
    peakRegIss,
    peakRegPromoIss
  } = forecastWeeklyFieldsVisibility(optConf);

  return [
    {
      caption: '',
      children: [true, props.description]
    },
    {
      caption: '',
      children: [props.corpStatus, props.storeStatus, props.totalCommitments]
    },
    ...(blockingList.length
      ? [
        {
          caption: 'preAssortment.columnsFieldsDrawer.blockingAttributes',
          children: blockingList.map((blockingName: string) => {
            const field = blockingName.replace(/\s/g, '');

            return props[field] ? props[field]! : false;
          }),
          tooltipText: 'preAssortment.tooltips.blockingAttributes',
        },
      ]
      : []),
    {
      caption: 'preAssortment.columnsFieldsDrawer.permanentLocation',
      children: [props.primary, props.secondary]
    },
    {
      caption: '',
      children: [props.crossMerch, props.extendedAssortment]
    },
    {
      caption: 'preAssortment.columnsFieldsDrawer.salesHistory',
      children: [props.shUnitAnnul, props.sh$Annul, props.campsUnitAnnul, props.lostSales$$, props.lostSalesUnits, props.shPromoPercent, ]
    },
    {
      caption: 'preAssortment.columnsFieldsDrawer.salesForecast',
      children: [
        props.sfUnitAnnul, props.sf$Annul, props.sfPromoPercent,
        (props.avgReg && avgReg) || props.avgReg,
        (props.avgRegPromo && avgRegPromo) || props.avgRegPromo,
        (props.avgRegIss && avgRegIss) || props.avgRegIss,
        (props.avgRegPromoIss && avgRegPromoIss) || props.avgRegPromoIss,
        (props.peakReg && peakReg) || props.peakReg,
        (props.peakRegPromo && peakRegPromo) || props.peakRegPromo,
        (props.peakRegIss && peakRegIss) || props.peakRegIss,
        (props.peakRegPromoIss && peakRegPromoIss) || props.peakRegPromoIss
      ]
    },
    {
      caption: '',
      children: [props.dgm, props.dmoq, props.promotable, ]
    },
    {
      caption: 'preAssortment.columnsFieldsDrawer.currentSetup',
      children: [props.bincap, props.q1f, props.fillTrips, props.wos, ]
    },
    ...(!isPostSolver && index === 0 ? [
      {
        caption: 'preAssortment.columnsFieldsDrawer.targetBincap',
        tooltipText: 'preAssortment.tooltips.targetBincap',
        children: [true, true, ]
      }
    ] : []),
    ...(isPostSolver ? [
      {
        caption: 'preAssortment.columnsFieldsDrawer.targetBincap',
        tooltipText: 'preAssortment.tooltips.targetBincap',
        children: [props.minTargetBincap, props.maxTargetBincap]
      }
    ] : []),
    ...(isPostSolver && index === 0 ? [
      {
        caption: 'postAssortment.columnsFieldsDrawer.optimized',
        children: [
          props.bincapOptimized,
          props.newMinTargetBincapOptimized,
          props.newMaxTargetBincapOptimized,
          props.totalFacingsOptimized,
          props.horzFacingsOptimized,
          props.vertFacingsOptimized,
          props.q1fOptimized,
          props.cappedFacingOptimized,
          props.cappedQ1fOptimized,
          props.fillTripsOptimized,
          props.wosOptimized
        ]
      },
      {
        caption: 'postAssortment.columnsFieldsDrawer.changes',
        children: [props.bincapChanges, props.fillTripsChanges, props.wosChanges]
      },
    ] : [])
  ];
};

const SECOND_LEVEL_HEADER = (
  props: Checkboxes,
  optConf: OptimizationConfiguration,
  blockingList: BlockingListState,
  isPostSolver = false,
  index = 0,
  envDate: string
) => {
  const {
    avgReg: showReg,
    avgRegPromo: showRegPromo,
    avgRegIss: showRegIss,
    avgRegPromoIss: showRegPromoIss,
    peakReg: showPeak,
    peakRegPromo: showPeakPromo,
    peakRegIss: showPeakIss,
    peakRegPromoIss: showPeakPromoIss
  } = forecastWeeklyFieldsVisibility(optConf);
  const totalCom = t('preAssortment.columnsFieldsDrawer.totalComm');

  return [
    {
      id: 'description',
      name: 'preAssortment.columnsFieldsDrawer.desc',
      isShowed: props.description,
      sortProperty: 'description',
    },
    {
      id: 'corpStatus',
      name: 'preAssortment.columnsFieldsDrawer.corpStatus',
      isShowed: props.corpStatus,
      sortProperty: 'corpStatus',
      tooltipText: 'preAssortment.tooltips.corpStatus'
    },
    {
      id: 'storeStatus',
      name: 'preAssortment.columnsFieldsDrawer.storeStatus',
      isShowed: props.storeStatus,
      sortProperty: 'storeStatus',
      tooltipText: 'preAssortment.tooltips.storeStatus'
    },
    {
      id: 'tcom',
      name: index !== 2 ? `${totalCom} \n${envDate}` : totalCom,
      isShowed: props.totalCommitments,
      sortProperty: 'totalCommitments',
      tooltipText: 'preAssortment.tooltips.totalComm'
    },
    ...(blockingList.length
      ? blockingList.map((blockingName: string) => {
        const field = blockingName.replace(/\s/g, '');

        return {
          id: field,
          name: blockingName,
          isShowed: props[field] ? props[field]! : false,
          sortProperty: field,
        };
      })
      : []),
    {
      id: 'primary',
      name: 'preAssortment.columnsFieldsDrawer.primary',
      isShowed: props.primary,
      sortProperty: 'primaryLocation',
      tooltipText: 'preAssortment.tooltips.primary'
    },
    {
      id: 'secondary',
      name: 'preAssortment.columnsFieldsDrawer.secondary',
      isShowed: props.secondary,
      sortProperty: 'secondaryLocations',
      tooltipText: 'preAssortment.tooltips.secondary'
    },
    {
      id: 'crossMerch',
      name: 'preAssortment.columnsFieldsDrawer.crossMerch',
      isShowed: props.crossMerch,
      sortProperty: 'crossMerch',
      tooltipText: 'preAssortment.tooltips.crossMerch'
    },
    {
      id: 'extendedAssortment',
      name: 'preAssortment.columnsFieldsDrawer.extendedAssortment',
      isShowed: props.extendedAssortment,
      sortProperty: 'isExtendedAssortment'
    },

    //SALES HISTORY COLUMNS
    {
      id: 'shUnitAnnul',
      name: 'preAssortment.columnsFieldsDrawer.unitAnnual',
      isShowed: props.shUnitAnnul,
      sortProperty: 'historic',
      tooltipText: 'preAssortment.tooltips.unitAnnualHistory'
    },
    {
      id: 'sh$Annul',
      name: 'preAssortment.columnsFieldsDrawer.$$Annual',
      isShowed: props.sh$Annul,
      sortProperty: 'histTotalSalesDollar',
      tooltipText: 'preAssortment.tooltips.$$AnnualHistory'
    },
    {
      id: 'campsUnitAnnul',
      name: 'preAssortment.columnsFieldsDrawer.campsUnitAnnul',
      isShowed: props.campsUnitAnnul,
      sortProperty: 'campsUnitAnnul',
      tooltipText: 'preAssortment.tooltips.campsUnitAnnul'
    },
    {
      id: 'lostSales$$',
      name: 'preAssortment.columnsFieldsDrawer.lostSales$$',
      isShowed: props.lostSales$$,
      sortProperty: 'lostSales$$',
      tooltipText: 'preAssortment.tooltips.lostSales$$'
    },
    {
      id: 'lostSalesUnits',
      name: 'preAssortment.columnsFieldsDrawer.lostSalesUnits',
      isShowed: props.lostSalesUnits,
      sortProperty: 'lostSalesUnits',
      tooltipText: 'preAssortment.tooltips.lostSalesUnits'
    },
    {
      id: 'shPromoPercent',
      name: 'preAssortment.columnsFieldsDrawer.promo%',
      isShowed: props.shPromoPercent,
      sortProperty: 'pctPromoHist',
      tooltipText: 'preAssortment.tooltips.promo%History'
    },

    //SALES FORECAST COLUMNS
    {
      id: 'sfUnitAnnul',
      name: 'preAssortment.columnsFieldsDrawer.unitAnnual',
      isShowed: props.sfUnitAnnul,
      sortProperty: 'totalForecast',
      tooltipText: 'preAssortment.tooltips.unitAnnualForecast'
    },
    {
      id: 'sf$Annul',
      name: 'preAssortment.columnsFieldsDrawer.$$Annual',
      isShowed: props.sf$Annul,
      sortProperty: 'forecast',
      tooltipText: 'preAssortment.tooltips.$$AnnualForecast'
    },
    {
      id: 'sfPromoPercent',
      name: 'preAssortment.columnsFieldsDrawer.promo%',
      isShowed: props.sfPromoPercent,
      sortProperty: 'ofPromo',
      tooltipText: 'preAssortment.tooltips.promo%Forecast'
    },
    {
      id: 'avgReg',
      name: 'preAssortment.columnsFieldsDrawer.avgReg',
      isShowed: (props.avgReg && showReg) || props.avgReg,
      sortProperty: 'avgReg',
      tooltipText: 'preAssortment.tooltips.avgReg'
    },
    {
      id: 'avgRegPromo',
      name: 'preAssortment.columnsFieldsDrawer.avgRegPromo',
      isShowed: (props.avgRegPromo && showRegPromo) || props.avgRegPromo,
      sortProperty: 'avgRegPromo',
      tooltipText: 'preAssortment.tooltips.avgRegPromo'
    },
    {
      id: 'avgRegIss',
      name: 'preAssortment.columnsFieldsDrawer.avgRegIss',
      isShowed: (props.avgRegIss && showRegIss) || props.avgRegIss,
      sortProperty: 'avgRegIss',
      tooltipText: 'preAssortment.tooltips.avgRegIss'
    },
    {
      id: 'avgRegPromoIss',
      name: 'preAssortment.columnsFieldsDrawer.avgRegPromoIss',
      isShowed: (props.avgRegPromoIss && showRegPromoIss) || props.avgRegPromoIss,
      sortProperty: 'avgRegPromoIss',
      tooltipText: 'preAssortment.tooltips.avgRegPromoIss'
    },
    {
      id: 'peakReg',
      name: 'preAssortment.columnsFieldsDrawer.peakReg',
      isShowed: (props.peakReg && showPeak) || props.peakReg,
      sortProperty: 'peakReg',
      tooltipText: 'preAssortment.tooltips.peakReg',
    },
    {
      id: 'peakRegPromo',
      name: 'preAssortment.columnsFieldsDrawer.peakRegPromo',
      isShowed: (props.peakRegPromo && showPeakPromo) || props.peakRegPromo,
      sortProperty: 'peakRegPromo',
      tooltipText: 'preAssortment.tooltips.peakRegPromo'
    },
    {
      id: 'peakRegIss',
      name: 'preAssortment.columnsFieldsDrawer.peakRegIss',
      isShowed: (props.peakRegIss && showPeakIss) || props.peakRegIss,
      sortProperty: 'peakRegIss',
      tooltipText: 'preAssortment.tooltips.peakRegIss'
    },
    {
      id: 'peakRegPromoIss',
      name: 'preAssortment.columnsFieldsDrawer.peakRegPromoIss',
      isShowed: (props.peakRegPromoIss && showPeakPromoIss) || props.peakRegPromoIss,
      sortProperty: 'peakRegPromoIss',
      tooltipText: 'preAssortment.tooltips.peakRegPromoIss'
    },

    //NO TITLES COLUMNS
    {
      id: 'dgm',
      name: 'preAssortment.columnsFieldsDrawer.dgm',
      tooltipText: 'preAssortment.tooltips.dgm',
      isShowed: props.dgm,
      sortProperty: 'dgm'
    },
    {
      id: 'dmoq',
      name: 'preAssortment.columnsFieldsDrawer.minOrderQty',
      isShowed: props.dmoq,
      sortProperty: 'dmoq',
      tooltipText: 'preAssortment.tooltips.minOrderQty'
    },
    {
      id: 'promotable',
      name: 'preAssortment.columnsFieldsDrawer.prom',
      tooltipText: 'preAssortment.tooltips.skusInProtoC',
      isShowed: props.promotable,
      sortProperty: 'promotable',
    },

    //CURRENT SETUP COLUMNS
    {
      id: 'bincap',
      name: 'preAssortment.columnsFieldsDrawer.bincap',
      isShowed: props.bincap,
      sortProperty: 'bincap',
      tooltipText: 'preAssortment.tooltips.bincap'
    },
    {
      id: 'q1f',
      name: 'preAssortment.columnsFieldsDrawer.q1f',
      isShowed: props.q1f,
      sortProperty: 'q1f',
      tooltipText: 'preAssortment.tooltips.q1f'
    },
    {
      id: 'fillTrips',
      name: 'preAssortment.columnsFieldsDrawer.fillTrips',
      isShowed: props.fillTrips,
      sortProperty: 'fillTrips',
      tooltipText: 'preAssortment.tooltips.fillTrips'
    },
    {
      id: 'wos',
      name: 'preAssortment.columnsFieldsDrawer.wos',
      tooltipText: 'preAssortment.tooltips.forecastedWeekly',
      isShowed: props.wos,
      sortProperty: 'wos'
    },

    ...(!isPostSolver && index === 0 ? [
      //OPTIMIZED COLUMNS
      {
        id: 'minTargetBincap',
        name: 'preAssortment.columnsFieldsDrawer.minTargetBincap',
        isShowed: true,
        sortProperty: 'minTargetBincap'
      },
      {
        id: 'maxTargetBincap',
        name: 'preAssortment.columnsFieldsDrawer.maxTargetBincap',
        isShowed: true,
        sortProperty: 'maxTargetBincap'
      }
    ] : []),

    ...(isPostSolver ? [
      {
        id: 'minTargetBincap',
        name: 'preAssortment.columnsFieldsDrawer.minTargetBincap',
        isShowed: props.minTargetBincap,
        sortProperty: 'minTargetBincap'
      },
      {
        id: 'maxTargetBincap',
        name: 'preAssortment.columnsFieldsDrawer.maxTargetBincap',
        isShowed: props.maxTargetBincap,
        sortProperty: 'maxTargetBincap'
      }
    ] : []),

    ...(isPostSolver && index === 0 ? [
      //OPTIMIZED COLUMNS
      {
        id: 'bincapOptimized',
        name: 'postAssortment.columnsFieldsDrawer.nBincap',
        isShowed: props.bincapOptimized,
        sortProperty: 'optimized.bincap'
      },
      {
        id: 'newMinTargetBincapOptimized',
        name: 'postAssortment.columnsFieldsDrawer.newMinTargetBincapOptimized',
        isShowed: props.newMinTargetBincapOptimized,
        sortProperty: 'optimized.newMinTargetBincapOptimized'
      },
      {
        id: 'newMaxTargetBincapOptimized',
        name: 'postAssortment.columnsFieldsDrawer.newMaxTargetBincapOptimized',
        isShowed: props.newMaxTargetBincapOptimized,
        sortProperty: 'optimized.newMaxTargetBincapOptimized'
      },
      {
        id: 'totalFacingOptimized',
        name: 'postAssortment.columnsFieldsDrawer.totalFacings',
        isShowed: props.totalFacingsOptimized,
        sortProperty: 'optimized.facings',
        tooltipText: 'preAssortment.tooltips.totalFacings'
      },
      {
        id: 'horzFacingOptimized',
        name: 'postAssortment.columnsFieldsDrawer.horzF',
        isShowed: props.horzFacingsOptimized,
        sortProperty: 'optimized.horizontalFacing',
        tooltipText: 'preAssortment.tooltips.horzF'
      },
      {
        id: 'vertFacingOptimized',
        name: 'postAssortment.columnsFieldsDrawer.vertF',
        isShowed: props.vertFacingsOptimized,
        sortProperty: 'optimized.verticalFacing',
        tooltipText: 'preAssortment.tooltips.vertF'
      },
      {
        id: 'q1fOptimized',
        name: 'postAssortment.columnsFieldsDrawer.q1f',
        isShowed: props.q1fOptimized,
        sortProperty: 'optimized.q1f'
      },
      {
        id: 'cappedFacingOptimized',
        name: 'postAssortment.columnsFieldsDrawer.cappedF',
        isShowed: props.cappedFacingOptimized,
        sortProperty: 'optimized.cappedFacingOptimized',
        tooltipText: 'preAssortment.tooltips.cappedF'
      },
      {
        id: 'cappedQ1fOptimized',
        name: 'postAssortment.columnsFieldsDrawer.cappedQ1f',
        isShowed: props.cappedQ1fOptimized,
        sortProperty: 'optimized.cappedQ1fOptimized',
        tooltipText: 'preAssortment.tooltips.cappedQ1f'
      },
      {
        id: 'fillTripsOptimized',
        name: 'postAssortment.columnsFieldsDrawer.fillTrips',
        isShowed: props.fillTripsOptimized,
        sortProperty: 'optimized.fillTripsOptimized'
      },
      {
        id: 'wosOptimized',
        name: 'postAssortment.columnsFieldsDrawer.wos',
        isShowed: props.wosOptimized,
        sortProperty: 'optimized.wos',
        tooltipText: 'preAssortment.tooltips.forecastedWeekly',
      },

      //CHANGES COLUMNS
      {
        id: 'bincapChanges',
        name: 'postAssortment.columnsFieldsDrawer.bincap',
        isShowed: props.bincapChanges,
        sortProperty: 'optimized.bincapChange'
      },
      {
        id: 'fillTripsChanges',
        name: 'postAssortment.columnsFieldsDrawer.fillTrips',
        isShowed: props.fillTripsChanges,
        sortProperty: 'optimized.fillTripsChange',
      },
      {
        id: 'wosChanges',
        name: 'postAssortment.columnsFieldsDrawer.wos',
        isShowed: props.wosChanges,
        sortProperty: 'optimized.wosChange'
      },
    ] : []),
  ];
};

const forecastWeeklyFieldsVisibility = (optConf: OptimizationConfiguration): ForecastKeys => {
  const { demand, includeISS, includePromo } = optConf;
  const isAvgReg = demand === DemandOptions.Average;// && labels === LabelType.Regular;
  const isAvgPeak = demand === DemandOptions.Peak;// && labels === LabelType.Regular;
  let forecastFields = {
    avgReg: false,
    avgRegPromo: false,
    avgRegIss: false,
    avgRegPromoIss: false,
    peakReg: false,
    peakRegPromo: false,
    peakRegIss: false,
    peakRegPromoIss: false
  };

  if (isAvgReg) {
    if (includeISS && includePromo) {
      forecastFields = { ...forecastFields, avgReg: true, avgRegPromoIss: true };
    } else if (includeISS && !includePromo) {
      forecastFields = { ...forecastFields, avgReg: true, avgRegIss: true, avgRegPromoIss: true };
    } else if (!includeISS && includePromo) {
      forecastFields = { ...forecastFields, avgReg: true, avgRegPromo: true, avgRegPromoIss: true };
    } else if (!includeISS && !includePromo) {
      forecastFields = { ...forecastFields, avgReg: true };
    }
  } else if (isAvgPeak) {
    if (includeISS && includePromo) {
      forecastFields = { ...forecastFields, peakReg: true, peakRegPromoIss: true };
    } else if (includeISS && !includePromo) {
      forecastFields = { ...forecastFields, peakReg: true, peakRegIss: true, peakRegPromoIss: true };
    } else if (!includeISS && includePromo) {
      forecastFields = { ...forecastFields, peakReg: true, peakRegPromo: true, peakRegPromoIss: true };
    } else if (!includeISS && !includePromo) {
      forecastFields = { ...forecastFields, peakReg: true };
    }
  }

  return forecastFields;
};

const getFormattedValue = (value: number | undefined, percentage: boolean = false, maximumFractionDigits: number = 2, currency: boolean = false): string => {
  if (!value && value != 0) {
    return '';
  }

  if (percentage) {
    /* istanbul ignore next */
    return value?.toLocaleString('en', { style: 'percent' });
  } else if (currency) {
    /* istanbul ignore next */
    return value?.toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits, style: 'currency', currency: 'USD' });
  } else {
    /* istanbul ignore next */
    return value?.toLocaleString('en', { maximumFractionDigits });
  }
};

const isOptimizedSelected = ({
  bincapOptimized,
  q1fOptimized,
  totalFacingsOptimized,
  horzFacingsOptimized,
  vertFacingsOptimized,
  // targetFacingsOptimized,
}: Checkboxes, postSolver: boolean): boolean =>
  postSolver && (
    q1fOptimized ? q1fOptimized : false ||
      bincapOptimized ? bincapOptimized : false ||
      totalFacingsOptimized ? totalFacingsOptimized : false ||
      horzFacingsOptimized ? horzFacingsOptimized : false ||
      vertFacingsOptimized ? vertFacingsOptimized : false
  // targetFacingsOptimized
  );

const isChangesSelected = ({
  bincapChanges, fillTripsChanges, wosChanges
}: Checkboxes, postSolver: boolean): boolean =>
  postSolver && (
    bincapChanges ? bincapChanges : false ||
    fillTripsChanges ? fillTripsChanges : false ||
    wosChanges ? wosChanges : false
  );

const selectForeCast = (optConf: OptimizationConfiguration): ForecastKeys => {
  const { demand, includeISS, includePromo } = optConf;
  const isAvgReg = demand === DemandOptions.Average;
  const isAvgPeak = demand === DemandOptions.Peak;
  let forecastFields = {
    avgReg: false,
    avgRegPromo: false,
    avgRegIss: false,
    avgRegPromoIss: false,
    peakReg: false,
    peakRegPromo: false,
    peakRegIss: false,
    peakRegPromoIss: false
  };

  if (isAvgReg) {
    if (includeISS && includePromo) {
      forecastFields = { ...forecastFields, avgRegPromoIss: true };
    } else if (includeISS && !includePromo) {
      forecastFields = { ...forecastFields, avgRegIss: true };
    } else if (!includeISS && includePromo) {
      forecastFields = { ...forecastFields, avgRegPromo: true };
    } else if (!includeISS && !includePromo) {
      forecastFields = { ...forecastFields, avgReg: true };
    }
  } else if (isAvgPeak) {
    if (includeISS && includePromo) {
      forecastFields = { ...forecastFields, peakRegPromoIss: true };
    } else if (includeISS && !includePromo) {
      forecastFields = { ...forecastFields, peakRegIss: true };
    } else if (!includeISS && includePromo) {
      forecastFields = { ...forecastFields, peakRegPromo: true };
    } else if (!includeISS && !includePromo) {
      forecastFields = { ...forecastFields, peakReg: true };
    }
  }

  return forecastFields;
};

export const getForecastVisibleKey = (optConf: OptimizationConfiguration) => {
  const visibleForeCast: ForecastKeys = selectForeCast(optConf);

  for (const [key, value] of Object.entries(visibleForeCast)) {
    if (value == true) {
      return key;
    }
  }

  return '';
};

const getScrollbarWidth = (): number => {
  const outer = document.createElement('div');

  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  document.body.appendChild(outer);

  const inner = document.createElement('div');

  outer.appendChild(inner);

  const scrollbarWidth = (outer.offsetWidth - inner.offsetWidth);

  outer?.parentNode?.removeChild(outer);

  return scrollbarWidth;
};

function orderByWithEmptyHandling<T> (
  items: T[],
  sortBy: string | number | undefined,
  order: 'asc' | 'desc' | undefined,
  sortByCb?: any
): T[] {

  if (order && sortBy) {
    const partitions = partition(
      items,
      row => {
        const value = get(row, sortBy, null);

        //Since 0 is considered as false we need to make 0 as true to handle negative numbers sorting.
        return value ? true : value === 0 ? true : false;
      }
    );

    return concat(
      order === 'asc' ? partitions[1] : [],
      orderBy(partitions[0], sortByCb ? sortByCb : sortBy, order),
      order === 'desc' ? partitions[1] : []
    );
  }

  return items;
}

const addBlockingAttributes = (fields: Checkboxes, blockingAttribute: Array<string>) => {
  blockingAttribute.forEach((attr: string) => {
    const key = attr.replace(/\s/g, '');

    // eslint-disable-next-line no-prototype-builtins
    if (!fields.hasOwnProperty(key) && attr.toLowerCase() !== 'none') {
      fields[key] = true;
    }
  });

  return fields;
};

const getProductNumsFitMirroringHeight = (mirroringHeight: number = 0, facingGroup: FacingGroup[]): string[] => {
  const { fitProductNums, noFitProductNums } = facingGroup
    .reduce((acc: { fitProductNums: string[], noFitProductNums: string[] }, { y, product_num }) => {
      if (y > mirroringHeight) acc.fitProductNums.push(product_num);
      if (y <= mirroringHeight) acc.noFitProductNums.push(product_num);

      return acc;
    }, { fitProductNums: [], noFitProductNums: [] });

  return difference(fitProductNums, noFitProductNums);
};

const getFixtureIdByXCoordinate = (newFacingX: number, fixtures: FixtureSpace[], { id: currentFixtureId, x: currentFixtureX, y: currentFixtureY, height: currentFixtureHeight }: LocatedFixture) => {
  if (newFacingX >= currentFixtureX) {
    return currentFixtureId;
  }

  const currentFixtureY2 = currentFixtureY + currentFixtureHeight;
  const neighboringFixture = fixtures.find(
    ({ x1, x2, y1, y2 }) => newFacingX >= x1 && newFacingX <= x2 && [y1, y2].includes(currentFixtureY2));

  return neighboringFixture?.id || currentFixtureId;
};

export {
  FIRST_LEVEL_HEADER,
  SECOND_LEVEL_HEADER,
  forecastWeeklyFieldsVisibility,
  getFormattedValue,
  isOptimizedSelected,
  isChangesSelected,
  getScrollbarWidth,
  orderByWithEmptyHandling,
  addBlockingAttributes,
  getProductNumsFitMirroringHeight,
  getFixtureIdByXCoordinate,
};
